'use client'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { normalizeVietnamese } from '@/lib/utils'
import type {
  TSearchFeaturedCollectionItem,
  TSearchFeaturedCollections,
} from '@/types/home'
import { ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'

// Desktop hiển thị tối đa 2 BST trên 1 hàng, nhiều hơn thì chuyển sang slide
const DESKTOP_MAX_STATIC_ITEMS = 2

export const getVisibleFeaturedCollections = (
  setting?: TSearchFeaturedCollections
): TSearchFeaturedCollectionItem[] => {
  if (!setting || setting.hidden) return []
  return (setting.items ?? []).filter(
    (item) => !item.hidden && item.title && item.url
  )
}

export const getFeaturedCollectionKeywords = (
  items: TSearchFeaturedCollectionItem[]
): string[] => {
  return items.flatMap((item) =>
    (item.keywords ?? '')
      .split(',')
      .map((keyword) => keyword.trim())
      .filter(Boolean)
  )
}

// BST khớp khi mọi từ trong từ khoá đều có trong title hoặc trong 1 từ khoá của BST
// (không phân biệt dấu, hoa thường)
export const filterFeaturedCollectionsByKeyword = (
  items: TSearchFeaturedCollectionItem[],
  keyword: string
): TSearchFeaturedCollectionItem[] => {
  const words = keyword.split(/\s+/).map((word) => normalizeVietnamese(word))
  if (!words.some(Boolean)) return []

  return items.filter((item) =>
    [item.title, ...getFeaturedCollectionKeywords([item])].some((text) => {
      const normalizedText = normalizeVietnamese(text)
      return words.every((word) => normalizedText.includes(word))
    })
  )
}

const FeaturedCollectionCard = ({
  item,
  setFalse,
}: {
  item: TSearchFeaturedCollectionItem
  setFalse: () => void
}) => {
  const t = useTranslations('header.search_bar')

  return (
    <Link
      href={item.url}
      onClick={setFalse}
      className='flex items-center gap-2 md:items-end'
      ga-tracking-value='navbar__search__bst'
      ga-tracking-label={`navbar__search__bst__${item.title}`}
    >
      <div className='relative h-[110px] w-[98px] shrink-0 overflow-hidden rounded-xl md:h-[114px] md:w-[164px]'>
        {/* Ảnh mobile riêng, thiếu thì dùng ảnh desktop */}
        <picture className='block h-full w-full'>
          {item.mb_image ? (
            <source
              media='(max-width: 767px)'
              srcSet={ImageHelper.getImageUrl(item.mb_image, '', 200)}
            />
          ) : null}
          <img
            src={ImageHelper.getImageUrl(
              item.image || '/placeholder.jpg',
              '',
              400
            )}
            alt={item.title}
            className='h-full w-full object-cover'
            width={164}
            height={114}
            loading='lazy'
          />
        </picture>
      </div>
      <div className='flex min-w-0 flex-1 flex-col items-start gap-1'>
        {item.is_new ? <TagThumbnail title='New' variant='primary' /> : null}
        <p className='text-base font-bold leading-5.5 text-neutral-900'>
          {item.title}
        </p>
        <span className='flex items-center gap-1 py-1 font-criteria text-xs text-neutral-500'>
          {t('view_collection')}
          <ArrowRight className='h-3 w-3' />
        </span>
      </div>
    </Link>
  )
}

export const FeaturedCollections = ({
  title,
  items,
  setFalse,
}: {
  title?: string
  items: TSearchFeaturedCollectionItem[]
  setFalse: () => void
}) => {
  const t = useTranslations('header.search_bar')
  const tButton = useTranslations('button')

  if (items.length === 0) return null

  const isDesktopSlide = items.length > DESKTOP_MAX_STATIC_ITEMS

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-4'>
        <p className='text-base font-medium'>
          {title || t('title.featured_collections')}
        </p>
        <Link
          href='/highlight-collections'
          onClick={setFalse}
          className='flex shrink-0 items-center gap-1 text-sm text-primary'
          ga-tracking-value='navbar__search__bst__view_all'
          ga-tracking-label='navbar__search__bst__view_all'
        >
          {tButton('view_all')}
          <ArrowRight className='h-4 w-4' />
        </Link>
      </div>

      {/* Mobile luôn xếp dọc; desktop xếp ngang, > 2 BST thì chuyển sang slide */}
      <div
        className={
          isDesktopSlide
            ? 'flex flex-col gap-4 md:hidden'
            : 'flex flex-col gap-4 md:flex-row md:gap-5 md:[&>*]:flex-1'
        }
      >
        {items.map((item, index) => (
          <FeaturedCollectionCard
            key={`${item.url}-${index}`}
            item={item}
            setFalse={setFalse}
          />
        ))}
      </div>

      {isDesktopSlide ? (
        <Carousel
          opts={{ align: 'start', slidesToScroll: 1 }}
          className='relative hidden w-full md:block'
        >
          <CarouselContent className='-ml-5'>
            {items.map((item, index) => (
              <CarouselItem
                key={`${item.url}-${index}`}
                className='basis-1/2 pl-5'
              >
                <FeaturedCollectionCard item={item} setFalse={setFalse} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious hideIfStart />
          <CarouselNext hideIfEnd />
        </Carousel>
      ) : null}
    </div>
  )
}
